import { nowSeconds } from "@/utils/time"
import type { NostrEvent } from "@/nostr/types"

/**
 * Time axis of the unified filter architecture.
 *
 * Everything is expressed in **event `created_at`** seconds (never in "time the
 * client received the post"), so the relay-side `since`/`until` bounds and the
 * client-side check always agree.
 */
export const TIME_RANGES = ["all", "hour", "day", "week", "month", "custom"] as const

export type TimeRange = (typeof TIME_RANGES)[number]

/** Relative windows, in seconds. `all` and `custom` have no fixed length. */
export const RANGE_SECONDS: Record<"hour" | "day" | "week" | "month", number> = {
  hour: 3_600,
  day: 86_400,
  week: 604_800,
  month: 2_592_000,
}

/** Explicit bounds, in `created_at` seconds. */
export interface CustomRange {
  start?: number
  end?: number
}

export interface TimeBounds {
  since?: number
  until?: number
}

/**
 * Relay-side bounds for a selection.
 *
 * `since` is the exact lower bound and can be applied by every relay. `until`
 * only exists for a custom range; pagination keeps using its own cursor, which
 * is always `<=` this value.
 */
export function timeBounds(
  range: TimeRange | undefined,
  custom: CustomRange | undefined,
  now: number = nowSeconds(),
): TimeBounds {
  if (!range || range === "all") return {}
  if (range === "custom") {
    const bounds: TimeBounds = {}
    if (typeof custom?.start === "number" && Number.isFinite(custom.start)) bounds.since = custom.start
    if (typeof custom?.end === "number" && Number.isFinite(custom.end)) bounds.until = custom.end
    return bounds
  }
  return { since: now - RANGE_SECONDS[range] }
}

/** True when the event falls inside the selection. */
export function matchesTimeRange(
  event: NostrEvent,
  range: TimeRange | undefined,
  custom?: CustomRange,
  now: number = nowSeconds(),
): boolean {
  if (!range || range === "all") return true
  const { since, until } = timeBounds(range, custom, now)
  if (since !== undefined && event.created_at < since) return false
  if (until !== undefined && event.created_at > until) return false
  return true
}

/** Inclusive bound helper shared by the date inputs. */
export function clampSeconds(value: number): number {
  return Math.max(0, Math.floor(value))
}
